import { localStorage as safeLocalStorage } from 'utils/browserStorage';
import React, { useState, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import DescriptionIcon from '@mui/icons-material/Description';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import SearchIcon from '@mui/icons-material/Search';
import SchoolIcon from '@mui/icons-material/School';
import { NotificationBell } from '../Notifications';
import { isNativeApp } from '../../utils/nativeApp';
import styles from './DentistNavbar.module.scss';
import logoBlue from '../../images/logoBlue.svg';
import DentistSideMenuHeader from '../DentistSideMenu/DentistSideMenuHeader';
import { useOfficeCeDirectoryAccess } from '../../pages/dentist/OfficeCeCoursesPage/useOfficeCeDirectoryAccess';

export interface DentistNavbarProps {
  /** User's first name for display in side menu */
  firstName?: string;
  /** User's last name for display in side menu */
  lastName?: string;
  /** User's photo URL for side menu avatar */
  photoUrl?: string;
  /** Optional page title shown centered in the navbar */
  title?: string;
  /** Callback when "Get a Temp" button is clicked */
  onGetTempClick?: () => void;
  /** Callback when "Hire Staff" menu item is clicked */
  onHireStaffClick?: () => void;
  /** Whether to show the "Get a Temp" button */
  showGetTempButton?: boolean;
  /** Custom action button instead of "Get a Temp" */
  customActionButton?: React.ReactNode;
}

const DentistNavbar: React.FC<DentistNavbarProps> = ({
  // firstName/lastName/photoUrl props are retained for back-compat but the side
  // menu header now shows the office identity (location count / practiceName)
  // via the shared DentistSideMenuHeader (T2-6189).
  title,
  onGetTempClick,
  onHireStaffClick,
  showGetTempButton = true,
  customActionButton,
}) => {
  const history = useHistory();
  const location = useLocation();
  const [isSideMenuOpen, setIsSideMenuOpen] = useState(false);
  const ceDirectoryAccess = useOfficeCeDirectoryAccess();

  const toggleSideMenu = useCallback(() => {
    setIsSideMenuOpen(prev => !prev);
  }, []);

  const handleGetTemp = useCallback(() => {
    if (onGetTempClick) {
      onGetTempClick();
    }
  }, [onGetTempClick]);

  const handleHireStaff = useCallback(() => {
    if (onHireStaffClick) {
      toggleSideMenu();
      onHireStaffClick();
    } else {
      toggleSideMenu();
      (window as any).TempStars?.App?.goReact('jobBoard');
    }
  }, [onHireStaffClick, toggleSideMenu]);

  const isMobile = isNativeApp();

  return (
    <>
      <div className={styles.navShell}>
        {/* Header */}
        <header className={`${styles.header} ${isMobile ? `${styles.headerMobile} headerMobile` : ''}`}>
          <div className={styles.headerBar}>
            <div className={styles.headerLeft}>
            <button
              className={styles.logoButton}
              onClick={() => history.push({ pathname: 'dentistHome' })}
              aria-label="Go to home"
            >
              <img src={logoBlue} alt="TempStars" className={styles.logo} />
            </button>
          </div>

          {title && !isMobile ? (
            <div className={styles.headerCenter} aria-label="Page title">
              <div className={styles.pageTitle}>{title}</div>
            </div>
          ) : (
            <div className={styles.headerCenter} />
          )}

          <div className={styles.headerActions}>
          {customActionButton ? (
            customActionButton
          ) : showGetTempButton ? (
            <button
              className={`${styles.getTempButton} ${isMobile ? styles.getTempButtonMobile : ''}`}
              onClick={handleGetTemp}
              aria-label="Get a Temp"
            >
              <span>+</span> {isMobile ? 'Temp' : 'Get a Temp'}
            </button>
          ) : null}

          <NotificationBell />

          <div className={styles.headerSeparator}></div>

          <button className={styles.menuButton} onClick={toggleSideMenu} aria-label="Open menu">
            <div className={styles.hamburger}>
              <span className={styles.hamburgerLine}></span>
              <span className={styles.hamburgerLine}></span>
              <span className={styles.hamburgerLine}></span>
            </div>
          </button>
          </div>
        </div>
        </header>
        {title && isMobile && (
          <h1 className={styles.pageTitleBelowNav}>{title}</h1>
        )}
      </div>

      {/* Side Menu */}
      <div className={`${styles.sideMenu} ${isMobile ? styles.sideMenuNative : ''} ${isSideMenuOpen ? styles.open : ''}`}>
        <DentistSideMenuHeader />
        <nav className={styles.sideMenuNav}>
          <a
            href="#"
            className={`${styles.menuItem} ${location.pathname.includes('dentistHome') || location.pathname === '/' ? styles.active : ''}`}
            onClick={(e) => {
              e.preventDefault();
              toggleSideMenu();
              history.push({ pathname: 'dentistHome' });
            }}
          >
            <HomeIcon />
            <span>Home</span>
          </a>

          <a
            href="#"
            className={`${styles.menuItem} ${location.pathname.includes('dentistLocations') ? styles.active : ''}`}
            onClick={(e) => {
              e.preventDefault();
              toggleSideMenu();
              (window as any).TempStars?.App?.goReact('dentistLocations');
            }}
          >
            <LocationOnIcon />
            <span>Locations</span>
          </a>

          <a
            href="#"
            className={`${styles.menuItem} ${location.pathname.includes('dentistInvoices') ? styles.active : ''}`}
            onClick={(e) => {
              e.preventDefault();
              toggleSideMenu();
              (window as any).TempStars?.App?.goReact('dentistInvoices');
            }}
          >
            <DescriptionIcon />
            <span>Invoices</span>
          </a>

          {ceDirectoryAccess.enabled && (
            <a
              href="#"
              className={`${styles.menuItem} ${location.pathname.includes('dentistCeCourses') ? styles.active : ''}`}
              onClick={(e) => {
                e.preventDefault();
                toggleSideMenu();
                const goReact = (window as any).TempStars?.App?.goReact;
                if (typeof goReact === 'function') goReact('dentistCeCourses');
                else history.push({ pathname: 'dentistCeCourses' });
              }}
            >
              <SchoolIcon />
              <span>CE Courses</span>
            </a>
          )}

          <a
            href="#"
            className={`${styles.menuItem} ${location.pathname.includes('jobBoard') || location.pathname.includes('hireStaff') ? styles.active : ''}`}
            onClick={(e) => {
              e.preventDefault();
              handleHireStaff();
            }}
          >
            <WorkOutlineIcon />
            <span>Hire Staff</span>
          </a>

          <a
            href="#"
            className={`${styles.menuItem} ${location.pathname.includes('dentistContactSupport') ? styles.active : ''}`}
            onClick={(e) => {
              e.preventDefault();
              toggleSideMenu();
              history.push({ pathname: 'dentistContactSupport' });
            }}
          >
            <HelpOutlineIcon />
            <span>Support</span>
          </a>

          {(() => {
            const ts: any = (window as any).TempStars;
            const user = ts?.User?.getCurrentUser?.();
            const isTest = Array.isArray(user?.locations) && user.locations.some((loc: any) => Number(loc?.isTest) === 1);
            if (!isTest) return null;
            return (
              <a
                href="#"
                className={styles.menuItem}
                onClick={(e) => {
                  e.preventDefault();
                  toggleSideMenu();
                  try { (window as any).TempStars?.App?.goReact('MixpanelLogs'); }
                  catch { history.push({ pathname: 'MixpanelLogs' }); }
                }}
              >
                <SearchIcon />
                <span>MixPanel Logs</span>
                <span className={styles.testOnly}>(Test Only)</span>
              </a>
            );
          })()}

          <a
            href="#"
            className={`${styles.menuItem} ${styles.logOutItem}`}
            onClick={(e) => {
              e.preventDefault();
              toggleSideMenu();
              if ((window as any).TempStars?.User?.logout) {
                (window as any).TempStars.User.logout().then(() => {
                  window.location.href = '/';
                }).catch(() => {
                  safeLocalStorage.clear();
                  window.location.href = '/';
                });
              } else {
                safeLocalStorage.clear();
                window.location.href = '/';
              }
            }}
          >
            <ExitToAppIcon />
            <span>Log Out</span>
          </a>
        </nav>
      </div>

      {/* Overlay for side menu */}
      {isSideMenuOpen && (
        <div className={styles.overlay} onClick={toggleSideMenu} />
      )}
    </>
  );
};

export default DentistNavbar;
